/* eslint-disable no-unused-vars */
import React, {
  useState, useRef, useEffect, useMemo,
} from 'react';
import {
  Box, Divider, MenuItem, TextField, Typography, IconButton, Collapse,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import {
  Edit as EditIcon, Settings, ExpandMore, ChevronRight,
} from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ListLayout from '../ListLayout';
import useStyles from '../../styles';
import CustomButton from '../../../../../components/General/CustomButton';
import ListTile from '../ListTile';
import UpsertFarmModal from '../Farm/UpsertFarmModal';
import { ReduxState } from '../../../../../types';
import AddSectorFlow from './AddSectorFlow';
import { setAccountFarm } from '../../../../../actions/accountFarmAction';
import { FarmSchema } from '../../../../../requests/api/apiTypes';
import { setFarmDevice } from '../../../../../actions/farmSectorActions';
import { setCurrentSector } from '../../../../../actions/currentSectorActions';
import SyncDeviceModal from '../Devices/SyncDeviceModal';
import useNodes from '../../../../../hooks/useNodes';
import useSensors from '../../../../../hooks/useSensors';
import { Equipo } from '../../../Riego/equipoColors';

// Tipo de un sector tal como viene en selectedFarm.sectors (array inline del schema).
type FarmSector = NonNullable<NonNullable<ReduxState['accountFarm']['selectedFarm']>['sectors']>[number];

// Orden por número de sector (S1, S2, S3…); sin número va al final.
const NO_SECTOR_NUMBER_ORDER = Number.MAX_SAFE_INTEGER;
const sectorNum = (s: FarmSector): number => {
  const n = Number(s.sectorNumber);
  return Number.isFinite(n) ? n : NO_SECTOR_NUMBER_ORDER;
};

const SectorsList = (
  {
    isLayout,
    isIrrigationView = false,
    equipos,
    equipoBySectorId,
  }: {
    isLayout?: boolean
    isIrrigationView?: boolean
    equipos?: Equipo[]
    equipoBySectorId?: Record<string, Equipo>
  },
) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const listContainerRef = useRef<HTMLDivElement>(null);

  const { selectedFarm, farmsOptions } = useSelector((state: ReduxState) => state.accountFarm);
  const { selectedSector } = useSelector((state: ReduxState) => state.currentSector);
  const [openAddSectorModal, setOpenAddSectorModal] = useState(false);
  const [openSyncModal, setOpenSyncModal] = useState(false);
  const { nodes, refetchNodes } = useNodes(selectedFarm?.id ?? '');
  const { sensors, refetchSensors } = useSensors(
    selectedFarm?.id ?? '',
  );

  const [upsertFarmModalState, setUpsertFarmModalState] = useState({ open: false, toEdit: false });

  const handleFarmValueChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (farmsOptions && e.target.value !== '0') {
      const auxFarm = farmsOptions
        .find((elem) => elem.id === e.target.value);
      dispatch(setAccountFarm(auxFarm as FarmSchema));
      dispatch(setCurrentSector(null));
    }
  };

  const handleSectorClick = (sectorId: string) => {
    if (selectedSector?.id === sectorId) {
      dispatch(setCurrentSector(null));
      dispatch(setFarmDevice(''));
    } else {
      // Find the sector object in the selectedFarm
      const selectedSectorObj = selectedFarm?.sectors?.find((sector) => sector.id === sectorId);

      if (selectedSectorObj) {
        dispatch(setCurrentSector(selectedSectorObj)); // Set current sector in Redux
      }
    }
  };

  useEffect(() => {
    if (selectedSector) {
      const sectorElement = document.getElementById(`sector-${selectedSector}`);
      if (sectorElement && listContainerRef.current) {
        sectorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, [selectedSector]);

  const handleSeeMore = (sectorId: string) => {
    const source = isLayout ? 'layout' : 'inicio';
    localStorage.setItem('sector-tab', '2');
    history.push(`/sectores/${sectorId}?source=${source}`
      + `&farmId=${selectedFarm?.id}`);
  };

  const handleIrrigationSeeMore = (sectorId: string) => {
    const source = isLayout ? 'layout' : 'inicio';
    localStorage.setItem('sector-tab', '8');
    history.push(`/sectores/${sectorId}?source=${source}`
      + `&farmId=${selectedFarm?.id}`);
  };

  // Render de una fila de sector (reutilizado por el listado plano y el agrupado).
  const renderSectorTile = (sector: FarmSector) => {
    const {
      id, name, sectorDevice, sectorNumber, variety,
    } = sector;
    let customSeeMoreIcon;
    if (selectedSector?.id === id) {
      if (isIrrigationView) {
        customSeeMoreIcon = (
          <IconButton onClick={() => handleIrrigationSeeMore(id ?? '')}>
            <VisibilityIcon sx={{ color: 'white', fontSize: '30px' }} />
          </IconButton>
        );
      } else {
        customSeeMoreIcon = (
          <IconButton onClick={() => handleSeeMore(id ?? '')}>
            <Settings sx={{ color: 'white', fontSize: '30px' }} />
          </IconButton>
        );
      }
    }
    return (
      <React.Fragment key={sector.wiseconnId ?? id}>
        <div id={`sector-${id}`}>
          <ListTile
            title={`E${sectorDevice || ''} - S${sectorNumber || ''} ${variety?.species ?? ''} ${variety?.name ?? ''}`}
            tileInformation={{
              'Nombre cliente': name,
            }}
            isActive={selectedSector?.id === id}
            onClick={() => handleSectorClick(id ?? '')}
            customContainerClassName={classes.actionableListTileContainer}
            customSeeMoreIcon={customSeeMoreIcon}
          />
          <Divider />
        </div>
      </React.Fragment>
    );
  };

  // Agrupa los sectores del campo por equipo, respetando el orden de `equipos`.
  const isGrouped = isIrrigationView && !!equipos?.length && !!equipoBySectorId;
  const sectorsByEquipoId = useMemo(() => {
    if (!isGrouped) return null;
    const map = new Map<Equipo['id'], FarmSector[]>();
    (equipos ?? []).forEach((eq) => map.set(eq.id, []));
    (selectedFarm?.sectors ?? []).forEach((sector) => {
      const eq = sector.id ? equipoBySectorId?.[sector.id] : undefined;
      if (!eq) return;
      const bucket = map.get(eq.id);
      if (bucket) bucket.push(sector);
    });
    map.forEach((arr) => arr.sort((a, b) => sectorNum(a) - sectorNum(b)));
    return map;
  }, [isGrouped, equipos, equipoBySectorId, selectedFarm?.sectors]);

  // Equipos desplegados en el lateral. Por defecto colapsados (solo se ven los equipos).
  const [expandedEquipos, setExpandedEquipos] = useState<Set<Equipo['id']>>(new Set());
  const toggleEquipo = (id: Equipo['id']) => setExpandedEquipos((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  // Al seleccionar un sector (p.ej. desde el mapa), abrir su equipo automáticamente.
  useEffect(() => {
    if (selectedSector?.id && equipoBySectorId) {
      const eq = equipoBySectorId[selectedSector.id];
      if (eq) setExpandedEquipos((prev) => (prev.has(eq.id) ? prev : new Set(prev).add(eq.id)));
    }
  }, [selectedSector?.id, equipoBySectorId]);

  useEffect(() => {
    if (farmsOptions?.length && !selectedFarm) {
      const storedFarmId = localStorage.getItem('selectedFarmId');
      if (storedFarmId) {
        const storedFarm = farmsOptions.find((farm) => farm.id === storedFarmId);
        if (storedFarm) {
          dispatch(setAccountFarm(storedFarm));
          return;
        }
      }
      dispatch(setAccountFarm(farmsOptions[0]));
    }
  }, [farmsOptions, selectedFarm, dispatch]);

  return (
    <>
      <ListLayout>
        <Box display="flex" justifyContent="space-between"
          alignItems="center" gap={3}>
          <Typography
            variant="h4"
            color="primary"
          >
            Sectores
          </Typography>
          {isLayout && selectedFarm && (
            <CustomButton
              buttonText='Sincronizar'
              onClick={() => setOpenSyncModal(true)}
              color='blue'
            />
          )}
        </Box>
        <Divider className={classes.divider} />
        <TextField
          value={selectedFarm?.id || ''}
          onChange={handleFarmValueChange}
          variant='standard'
          fullWidth
          select
          label='Campo'
          sx={{ marginBottom: '5px' }}
        >
          {farmsOptions
            ?.map((elem) => (<MenuItem key={elem.id} value={elem.id}>
              {elem.name}
            </MenuItem>))}
          {isLayout && <>
            <Divider className={classes.divider} />
            <MenuItem value={'0'} onClick={() => setUpsertFarmModalState({ open: true, toEdit: false })}>
              Crear nuevo campo
            </MenuItem>
          </>}
        </TextField>
        {selectedFarm && isLayout && <>
          <ListTile
            tileInformation={{
              Nombre: selectedFarm?.name as string,
              Hectareas: `${selectedFarm?.theoreticalSurface} HA` || 'S/I',
              Ubicación: selectedFarm?.location as string,
              'N° Sectores': selectedFarm?.sectors?.length ?? 0,
            }}
            customSeeMoreIcon={(
              <IconButton onClick={() => setUpsertFarmModalState({ open: true, toEdit: true })}>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          />
          <Divider className={classes.divider} />
          {isLayout && (
            <Box marginBottom='12px' marginTop="12px" width='100%'>
              <CustomButton buttonText='AGREGAR SECTOR' onClick={() => setOpenAddSectorModal(true)} color='blue' />
            </Box>
          )}
        </>}
        {!selectedFarm && <Typography variant='body2'>
          Selecciona un campo para más información
        </Typography>}
        <Box className={classes.listTilesContainer} ref={listContainerRef}>
          {/* Render agrupado por equipo (Vista General de Riego) */}
          {isGrouped && sectorsByEquipoId
            && (equipos ?? []).map((equipo) => {
              const equipoSectors = sectorsByEquipoId.get(equipo.id) ?? [];
              if (equipoSectors.length === 0) return null;
              const isOpen = expandedEquipos.has(equipo.id);
              return (
                <Box key={equipo.id} sx={{ mb: 1 }}>
                  {/* Encabezado del equipo (clickable): despliega/colapsa sus sectores */}
                  <Box
                    onClick={() => toggleEquipo(equipo.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 1,
                      mb: 0.5,
                      px: 1,
                      py: 0.75,
                      borderRadius: 1,
                      cursor: 'pointer',
                      backgroundColor: '#F1F5F9',
                      '&:hover': { backgroundColor: '#E2E8F0' },
                    }}
                  >
                    <Box sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: equipo.color,
                      flexShrink: 0,
                    }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, flexGrow: 1 }}>
                      {equipo.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      {equipoSectors.length}
                    </Typography>
                    {isOpen ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                  </Box>
                  <Collapse in={isOpen} unmountOnExit>
                    {equipoSectors.map((sector) => renderSectorTile(sector))}
                  </Collapse>
                </Box>
              );
            })}

          {/* Render plano (resto de vistas y cuando no hay agrupamiento) */}
          {!isGrouped && (selectedFarm?.sectors && selectedFarm.sectors.length > 0)
            && (isIrrigationView
              ? [...selectedFarm.sectors].sort((a, b) => sectorNum(a) - sectorNum(b))
              : selectedFarm.sectors
            ).map((sector) => renderSectorTile(sector))}

          {(selectedFarm?.sectors && selectedFarm.sectors.length === 0)
            && <Typography variant='body2'>
              No hay sectores asociados a este campo
            </Typography>}
        </Box>
      </ListLayout>
      <AddSectorFlow
        isModalOpen={openAddSectorModal}
        setOpenModal={setOpenAddSectorModal}
        lat={selectedFarm?.center.lat ?? ''}
        lng={selectedFarm?.center.lng ?? ''}
      />
      <UpsertFarmModal
        modalState={upsertFarmModalState}
        setModalState={setUpsertFarmModalState}
      />
      {openSyncModal && (
        <SyncDeviceModal
          openSyncDevicesModal={openSyncModal}
          nodes={nodes}
          sensors={sensors}
          setOpenSyncDevicesModal={setOpenSyncModal}
          classes={classes}
          onSync={() => { refetchNodes(); refetchSensors(); }}
        />
      )}
    </>
  );
};

export default SectorsList;
